import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { ImagePlus, Loader2, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useUploadFiles } from '@/domains/files/services/files';
import { ResourceType } from '@/shared/types/fileTypes';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 10;

interface UploadImagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  resourceType: ResourceType;
  resourceId: string;
}

type PreviewFile = {
  file: File;
  previewUrl: string;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const UploadImagesDialog = ({
  open,
  onOpenChange,
  onSuccess,
  resourceType,
  resourceId,
}: UploadImagesDialogProps) => {
  const uploadFiles = useUploadFiles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<PreviewFile[]>([]);

  const totalSizeLabel = useMemo(() => {
    const total = previews.reduce((sum, p) => sum + p.file.size, 0);
    return formatFileSize(total);
  }, [previews]);

  useEffect(() => {
    if (!open) {
      setPreviews((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        return [];
      });
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [open]);

  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (fileList: FileList | null) => {
    if (!fileList?.length) return;

    const next: PreviewFile[] = [];
    const errors: string[] = [];

    for (const file of Array.from(fileList)) {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" is not a supported image type.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`"${file.name}" exceeds the 10 MB limit.`);
        continue;
      }
      next.push({ file, previewUrl: URL.createObjectURL(file) });
    }

    setPreviews((prev) => {
      const room = Math.max(0, MAX_FILES - prev.length);
      const accepted = next.slice(0, room);
      next.slice(room).forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [...prev, ...accepted];
    });

    if (errors.length > 0) {
      showErrorWithApiError(new Error(errors[0]), ERROR.TERMINAL.UPLOAD_IMAGES);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setPreviews((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (previews.length === 0) return;

    try {
      await uploadFiles.mutateAsync({
        resourceType: resourceType,
        resourceId: resourceId,
        files: previews.map((p) => p.file),
      });
      showSuccess(SUCCESS.TERMINAL.UPLOAD_IMAGES);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.UPLOAD_IMAGES);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Upload Images</DialogTitle>
          <DialogDescription>
            Upload images for <strong>{resourceType?.split('_').join(' ')} - {resourceId}</strong>. JPEG, PNG or WebP up to 10 MB each. Max 10 images.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
            disabled={uploadFiles.isPending}
          />

          <div
            role="button"
            tabIndex={0}
            className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-8 text-center cursor-pointer hover:bg-muted/60 transition-colors"
            onClick={() => !uploadFiles.isPending && inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!uploadFiles.isPending) inputRef.current?.click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!uploadFiles.isPending) addFiles(e.dataTransfer.files);
            }}
          >
            <ImagePlus className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Click or drag images here</p>
            <p className="text-xs text-muted-foreground">
              Up to {MAX_FILES} images · {previews.length}/{MAX_FILES} selected
              {previews.length > 0 ? ` · ${totalSizeLabel}` : ''}
            </p>
          </div>

          {previews.length > 0 && (
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[280px] overflow-y-auto pr-1">
              {previews.map((preview, index) => (
                <li key={`${preview.file.name}-${index}`} className="relative group rounded-md border overflow-hidden bg-muted">
                  <img
                    src={preview.previewUrl}
                    alt={preview.file.name}
                    className="h-28 w-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1">
                    <p className="truncate text-[10px] text-white">{preview.file.name}</p>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-1 right-1 h-7 w-7 p-0 opacity-90"
                    onClick={() => removeFile(index)}
                    disabled={uploadFiles.isPending}
                    aria-label={`Remove ${preview.file.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={uploadFiles.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={uploadFiles.isPending || previews.length === 0} className="gap-2">
              {uploadFiles.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload {previews.length > 0 ? `(${previews.length})` : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadImagesDialog;
